import React, { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";

const OAuthCallback: React.FC = () => {
	const [searchParams] = useSearchParams();
	const hasProcessedRef = useRef(false);

	useEffect(() => {
		if (hasProcessedRef.current) return; // Prevent multiple executions

		const handleOAuthCallback = async () => {
			hasProcessedRef.current = true; // Mark as processed immediately

			const code = searchParams.get("code");
			const state = searchParams.get("state");
			const error = searchParams.get("error");

			if (error) {
				// OAuth error occurred
				const message = {
					type: "GOOGLE_OAUTH_ERROR",
					error: error,
				};
				// Send message to parent window with proper origin handling
				if (window.opener) {
					window.opener.postMessage(message, "*");
				}
				window.close();
				return;
			}

			if (!code || !state) {
				// Missing required parameters
				const message = {
					type: "GOOGLE_OAUTH_ERROR",
					error: "Missing authorization code or state",
				};
				// Send message to parent window with proper origin handling
				if (window.opener) {
					window.opener.postMessage(message, "*");
				}
				window.close();
				return;
			}

			try {
				// Call edge function to exchange code for tokens
				const response = await fetch(
					`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-oauth-callback`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
						},
						body: JSON.stringify({
							code,
							state,
						}),
					},
				);

				const result = await response.json();

				if (result.success) {
					// OAuth successful
					const successMessage = {
						type: "GOOGLE_OAUTH_SUCCESS",
						calendarId: result.data.calendar_id,
						calendarName: result.data.calendar_name,
					};
					// Send message to parent window with proper origin handling
					if (window.opener) {
						window.opener.postMessage(successMessage, "*");
					}
				} else {
					// OAuth failed
					const errorMessage = {
						type: "GOOGLE_OAUTH_ERROR",
						error: result.error,
					};
					// Send message to parent window with proper origin handling
					if (window.opener) {
						window.opener.postMessage(errorMessage, "*");
					}
				}
			} catch (error) {
				console.error("OAuth callback error:", error);
				console.error("Error details:", {
					name: error.name,
					message: error.message,
					stack: error.stack,
				});
				const errorMessage = {
					type: "GOOGLE_OAUTH_ERROR",
					error: "Failed to complete OAuth flow",
				};
				// Send message to parent window with proper origin handling
				if (window.opener) {
					window.opener.postMessage(errorMessage, "*");
				}
			}

			// Add a small delay before closing to ensure message is sent
			setTimeout(() => {
				window.close();
			}, 100);
		};

		handleOAuthCallback();
	}, [searchParams]);

	return (
		<div className="min-h-screen bg-gray-50 flex items-center justify-center">
			<div className="text-center">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
				<p className="text-gray-600">
					Completing Google Calendar connection...
				</p>
				<p className="text-sm text-gray-500 mt-2">Please wait...</p>
			</div>
		</div>
	);
};

export default OAuthCallback;
